import React, { useState } from 'react';
import { TrendingDownIcon, TrendingUpIcon } from 'lucide-react';
import { PeriodProgressPanel } from '../components/activity/PeriodProgressPanel';
import { RecentLogList } from '../components/activity/RecentLogList';
import { useActivityPlan } from '../contexts/ActivityPlanContext';
import { activityById } from '../data/activityCatalog';
import { actualForPeriod, paceOf, periodLabel, targetForPeriod } from '../utils/activityProgress';
import type { PlanPeriod } from '../types/activity';
const periods: PlanPeriod[] = ['week', 'month', 'quarter'];
export function ProgressPage() {
  const [period, setPeriod] = useState<PlanPeriod>('week');
  const {
    activeGoals,
    logs,
    plan
  } = useActivityPlan();
  const rows = activeGoals.map(goal => {
    const target = targetForPeriod(goal, period);
    const actual = actualForPeriod(logs, goal.category, period);
    return {
      label: activityById[goal.category].shortLabel,
      actual,
      target,
      pace: paceOf(actual, target)
    };
  });
  const behind = rows.filter(row => row.pace === 'behind');
  const ahead = rows.filter(row => row.pace === 'ahead');
  return <main className="w-full px-4 py-5 sm:px-6 sm:py-6 lg:px-8" data-id="element-832" data-mp="1xm18wr8umv3g">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-5" data-id="element-833" data-mp="21mbfa50jzniu">
        <header className="flex flex-wrap items-end justify-between gap-3" data-id="element-834" data-mp="26m7nkggaa147">
          <div data-id="element-835" data-mp="26k38r3gujhp5">
            <h1 className="text-2xl font-bold tracking-tight text-navy-900" data-id="element-836" data-mp="2blskklbw7zea">
              My Progress
            </h1>
            <p className="mt-1 text-sm text-navy-600" data-id="element-837" data-mp="2gju417d8e0qa">
              Activity against the plan you set · {plan.workdays} prospecting days
              per week
            </p>
          </div>
          <div role="tablist" aria-label="Reporting period" className="inline-flex rounded-lg border border-navy-200 bg-white p-1" data-id="element-838" data-mp="2pdgtygurc9rk">
            {periods.map(option => <button key={option} type="button" role="tab" aria-selected={period === option} onClick={() => setPeriod(option)} className={`inline-flex h-9 items-center rounded-md px-3 text-sm font-semibold capitalize ${period === option ? 'bg-navy-800 text-white' : 'text-navy-600 hover:bg-navy-50'}`} data-id="element-839" data-mp="2zc6ekhe1g8bi">
                {option}
              </button>)}
          </div>
        </header>

        <div className="grid gap-4 sm:grid-cols-2" data-id="element-840" data-mp="1dyjj4t8wotiq">
          <div className="rounded-2xl border border-navy-100 bg-white p-5 shadow-card" data-id="element-841" data-mp="1dzulqpqm7v89">
            <div className="flex items-center gap-2" data-id="element-842" data-mp="1rwrl5fksul0r">
              <TrendingUpIcon className="h-4 w-4 text-success-600" aria-hidden="true" data-id="element-843" data-mp="1my6kjf1mxzbs" />
              <h2 className="text-sm font-semibold text-navy-900" data-id="element-844" data-mp="3tpfqpvkx1kjp">
                Winning {periodLabel[period].toLowerCase()}
              </h2>
            </div>
            {ahead.length === 0 ? <p className="mt-2 text-sm text-navy-500" data-id="element-845" data-mp="248ka2bbjzox9">
                No goals fully met yet this period.
              </p> : <ul className="mt-2 space-y-1 text-sm text-navy-700" data-id="element-846" data-mp="1ui8rbin7nb6b">
                {ahead.map(row => <li key={row.label} data-id="element-847" data-mp="1zgk15tdi9loj">
                    <span className="font-semibold" data-id="element-848" data-mp="1zfhe5wjb03dw">{row.label}</span> —{' '}
                    {row.actual} of {row.target}
                  </li>)}
              </ul>}
          </div>

          <div className="rounded-2xl border border-navy-100 bg-white p-5 shadow-card" data-id="element-849" data-mp="1qeatdb9mg6eu">
            <div className="flex items-center gap-2" data-id="element-850" data-mp="1bkxus5syqvuk">
              <TrendingDownIcon className="h-4 w-4 text-warn-600" aria-hidden="true" data-id="element-851" data-mp="1ghoags0uvpfg" />
              <h2 className="text-sm font-semibold text-navy-900" data-id="element-852" data-mp="35xnpuqv2ib3j">
                Coaching focus
              </h2>
            </div>
            {behind.length === 0 ? <p className="mt-2 text-sm text-navy-500" data-id="element-853" data-mp="1au99l07n1hjh">
                Nothing behind pace. Consider raising a target.
              </p> : <ul className="mt-2 space-y-1 text-sm text-navy-700" data-id="element-854" data-mp="16qjlscysf3zi">
                {behind.map(row => <li key={row.label} data-id="element-855" data-mp="wu6rpdzbyo48">
                    <span className="font-semibold" data-id="element-856" data-mp="wt56whsiin9b">{row.label}</span> — short{' '}
                    {row.target - row.actual}
                  </li>)}
              </ul>}
          </div>
        </div>

        <PeriodProgressPanel period={period} showCoaching data-id="element-857" data-mp="n33tql1p6p96" />

        <RecentLogList limit={14} title="Activity history" data-id="element-858" data-mp="j20fu437n3al" />
      </div>
    </main>;
}